<script setup>
import { onUnmounted, watch } from 'vue';

import { DEV_MODE } from '@/config/dev-mode';

import { findTermsAPI } from '@/api/fortuneApi';
import { useStore } from 'vuex';
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePaymentsStore } from '@/store/modules/payments';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { useSajuData } from '@/composables/saju-insight/useSajuData';
import {
  useAnalysisServices,
  ANALYSIS_SERVICE_CONFIG,
} from '@/composables/saju-insight/useAnalysisServices';

/** 사주풀이 */
import CoverPageBasic from '@/components/saju-insight/basic/CoverPageBasic.vue';
import CoverPanelBasic from '@/components/saju-insight/basic/CoverPanelBasic.vue';
import DetailPageBasic from '@/components/saju-insight/basic/DetailPageBasic.vue';
import DetailPanelBasic from '@/components/saju-insight/basic/DetailPanelBasic.vue';
/** 성향분석 */
import overviewTendency from '@/components/saju-insight/personality/CoverPagePersonality.vue';
import overviewPaymentInfo from '@/components/saju-insight/personality/CoverPanelPersonality.vue';
import Personality from '@/components/saju-insight/personality/DetailPagePersonality.vue';
import SajuInsightPersPanelRight from '@/components/saju-insight/personality/DetailPanelPersonality.vue';
/** 용신분석 */
import overviewYongsin from '@/components/saju-insight/yongsin/CoverPageYongsin.vue';
import overviewPaymentInfoYoungsin from '@/components/saju-insight/yongsin/CoverPanelYongsin.vue';
import youngsin from '@/components/saju-insight/yongsin/DetailPageYongsin.vue';
import SajuInsightYongsinPanelRight from '@/components/saju-insight/yongsin/DetailPanelYongsin.vue';
/** 오행 */
import Five from '@/components/saju-insight/ohaeng/five.vue';
import FiveGuide from '@/components/saju-insight/ohaeng/fiveGuide.vue';
import FiveMy from '@/components/saju-insight/ohaeng/fiveMy.vue';
import fiveTab from '@/components/saju-insight/ohaeng/fiveTab.vue';
import fiveMyTab from '@/components/saju-insight/ohaeng/fiveMyTab.vue';
/** 신년운세 */
import CoverPageYearlyFortune from '@/components/saju-insight/yearly-fortune/CoverPageYearlyFortune.vue';
import CoverPanelYearlyFortune from '@/components/saju-insight/yearly-fortune/CoverPanelYearlyFortune.vue';
import DetailPageYearlyFortune from '@/components/saju-insight/yearly-fortune/DetailPageYearlyFortune.vue';
/** 낭만월운 */
import overviewMonthlyFortune from '@/components/saju-insight/monthly-fortune/CoverPageMonthlyFortune.vue';
import overviewPaymentInfoMonthlyFortune from '@/components/saju-insight/monthly-fortune/CoverPanelMonthlyFortune.vue';
import MonthlyFortune from '@/components/saju-insight/monthly-fortune/DetailPageMonthlyFortune.vue';
/** 낭만궁합 */
import CoverPageHarmony from '@/components/saju-insight/nangman-harmony/CoverPageHarmony.vue';
import CoverPanelHarmony from '@/components/saju-insight/nangman-harmony/CoverPanelHarmony.vue';
import DetailPageHarmony from '@/components/saju-insight/nangman-harmony/DetailPageHarmony.vue';
import DetailPanelHarmony from '@/components/saju-insight/nangman-harmony/DetailPanelHarmony.vue';
/** 용신라이트 */
import CoverPageYongsinLight from '@/components/saju-insight/yongsin-light/CoverPageYongsinLight.vue';
import CoverPanelYongsinLight from '@/components/saju-insight/yongsin-light/CoverPanelYongsinLight.vue';
import DetailPageYongsinLight from '@/components/saju-insight/yongsin-light/DetailPageYongsinLight.vue';
import DetailPanelYongsinLight from '@/components/saju-insight/yongsin-light/DetailPanelYongsinLight.vue';
/** 공통 */
import PopAnalysisInfo from '@/components/saju-insight/common/PopAnalysisInfo.vue';
import PopAnalysisInfoOther from '@/components/saju-insight/common/PopAnalysisInfoOther.vue';
import AlertComponent from '@/components/common/Alert.vue';


const route = useRoute();
const router = useRouter();

const store = useAnalysisStore();
const dataStore = useAnalysisDataStore();
const paymentsStore = usePaymentsStore();

const { selectedMenu } = storeToRefs(store);
const { birthData, birthEightTxt, paramData, sajuData, setSajuData } = storeToRefs(dataStore);

// ─── 상세분석 서비스 통합 composable ───────────────────────────────────────────
const {
  serviceData,
  activeServiceKey,
  activeContent: analysisContent,
  activeTicketData: analysisTicketData,
  activeTicketCount: analysisTicketCount,
  fetchAllContents,
  fetchAllAccess,
  ACCESS_TYPE_IDS,
  getTypeIdByMenu,
  isWithinFreePageLimit: tendencyGuide,
  hasPermissionByTypeId,
} = useAnalysisServices();

const guestUserId = computed(() => route.params.id);

const componentKey = ref(0);

const showPopAnalysisInfo = ref(false);

/** 선택된 설명 내비게이션 탭 - 'basic', 'image', 'detail' */
const selectedDescNav = ref('basic');
const selectedDescNavSub = ref('first');

// fiveMyTab 전용 상태
const selectedDescNavMy = ref('basic');
const selectedDescNavSubMy = ref('first');
const langModeMy = ref('chnkor');

const toggleTabOpenClass = ref('');
const toggleTabContent = () => {
  if (toggleTabOpenClass.value === '') {
    toggleTabOpenClass.value = 'wide';
  } else {
    toggleTabOpenClass.value = '';
  }
};

const updateSelectedDescNav = (val) => {
  selectedDescNav.value = val;
};
const updateSelectedDescNavSub = (val) => {
  selectedDescNavSub.value = val;
};

// fiveMyTab 전용 핸들러들
const updateSelectedDescNavMy = (val) => {
  selectedDescNavMy.value = val;
};
const updateSelectedDescNavSubMy = (val) => {
  selectedDescNavSubMy.value = val;
};
const updateLangModeMy = (val) => {
  langModeMy.value = val;
};

const handleClosePop = () => {
  showPopAnalysisInfo.value = false;
};
const handleChargePop = (shortageAmount) => {
  const path = '/purchase/points';
  const query = {
    next: `/analysis/${guestUserId.value}`,
    shortageAmount,
  };

  router.push({ path, query });
  showPopAnalysisInfo.value = false;
};
const showPopAnalysisInfoOther = ref(false);
const handleClosePopOther = () => {
  showPopAnalysisInfoOther.value = false;
};
const handleChargePopOtherProduct = () => {
  router.push('/purchase/tickets');
  showPopAnalysisInfoOther.value = false;
};

const showPopAnalysisInfoDo = async () => {
  // analysisContent가 없으면 처리하지 않음
  if (!analysisContent.value) {
    DEV_MODE && console.warn('analysisContent가 아직 로드되지 않았습니다.');
    return;
  }

  // 현재 서비스의 권한이 이미 있으면 팝업 표시 안 함
  if (hasPermissionByTypeId(analysisContent.value.analysisTypeId)) {
    showPopAnalysisInfo.value = false;
    showPopAnalysisInfoOther.value = false;
    return;
  }

  if (
    paymentsStore?.points < analysisContent.value?.pointPrice &&
    analysisTicketCount.value === 0
  ) {
    // 포인트가 적고, 분석권도 없을 때
    // 포인트 충전 팝업 표시 (분석권 구매 팝업 X)
    showPopAnalysisInfoOther.value = false;
    showPopAnalysisInfo.value = true;
  } else {
    // 포인트 또는 분석권이 충분할 때,
    // 분석권 구매 팝업 표시 (포인트 충전 팝업 X)
    showPopAnalysisInfoOther.value = true;
    showPopAnalysisInfo.value = false;
  }
};

const dialogAlert = ref(null);
const dialogTitle = ref('');
const dialogText = ref('');
const dialogNextUrl = ref('');

const showAlert = ({ title, nextUrl }) => {
  dialogTitle.value = title;
  dialogText.value = '';
  dialogAlert.value.openAlertDialog();
  dialogNextUrl.value = nextUrl;
};

/** 신년운세 경고 상태 */
const yearlyFortuneWarningMessage = ref('');

/** 신년운세 경고 응답 처리 (대운범위초과 등) */
const handleYearlyFortuneWarning = (message) => {
  yearlyFortuneWarningMessage.value = message;
  showAlert({
    title: message,
    nextUrl: () => {
      store.clickMainMenu('/overviewYearlyFortune');
    },
  });
};

// =============== 사주 데이터 ===============
const { initWithValidation } = useSajuData();

const initializeSajuData = () =>
  initWithValidation(guestUserId.value, {
    showAlert,
    onRestricted: () => router.push('/saju-service'),
  });

/** 상세분석 컨텐츠 불러오기 상태 */
const loadingPersonalityInfo = ref(true);

/** 상세분석 이용내역 정보 불러오기 */
const fetchAnalysisLogData = async (analysisTypeId = null) => {
  // log 호출이 불필요한 타입은 생략:
  // - useAccess 타입(3·4·9·41): access 응답의 analysisLogs로 이용내역 충당
  // - 사주풀이(8): 무료라 권한 판단 불필요 + logData 소비처 없음
  if (analysisTypeId && (ACCESS_TYPE_IDS.includes(analysisTypeId) || analysisTypeId === 8)) return;
  try {
    await store.getAnalysisLog(guestUserId.value, analysisTypeId);
  } catch (e) {
    DEV_MODE && console.error(`상세분석 > 이용내역 로드 실패: ${JSON.stringify(e)}`);
  }
};

// =============== 포인트 및 분석권 ===============

/** 포인트 및 분석권 정보 불러오기 */
const fetchPointAndTicketData = async () => {
  try {
    await Promise.all([
      paymentsStore.getOwnPoint(),
      paymentsStore.fetchTicketCount(),
    ]);
  } catch (e) {
    DEV_MODE && console.error(`상세분석 > 포인트 및 분석권 정보 로드 실패: ${JSON.stringify(e)}`);
  }
};

/**
 * 상세분석 데이터 초기화 함수
 *
 * - 사주정보 요청
 * - 포인트 및 분석권 요청
 */
const setupInit = async () => {
  loadingPersonalityInfo.value = true;
  try {
    const result = await initializeSajuData();
    if (result?.cancelled || result?.failed || result?.unknownTime || result?.futureSaju) return;
    // 선택된 메뉴에 따른 typeId로 이용내역 조회
    const typeId = getTypeIdByMenu(selectedMenu.value);
    // 모든 데이터(권한 포함)가 로드된 후에 loadingPersonalityInfo = false
    await Promise.all([
      fetchPointAndTicketData(),
      fetchAnalysisLogData(typeId),
      fetchAllContents(),
      fetchAllAccess(guestUserId.value),
    ]);
  } catch (e) {
    DEV_MODE && console.error('상세분석 > 초기화 실패:', e);
  } finally {
    loadingPersonalityInfo.value = false;
  }
};

onMounted(async () => {
  const vuexStore = useStore();
  const userId = vuexStore.getters['user/id'];
  if (!userId) return;

  if (!route.params?.id) {
    router.push('/saju-service');
    return;
  }

  // query param의 menu를 setupInit 전에 먼저 적용하여
  // 이전 분석 화면이 잠깐 보이는 현상 방지
  if (route.query?.menu) {
    store.clickMainMenu(route.query?.menu);
  }

  await setupInit();
});

onUnmounted(() => {
  dataStore.resetAll();
});

/** 게스트 유저 변경 시 데이터 초기화 및 재요청 */
watch(guestUserId, async (newId, oldId) => {
  if (newId && oldId && newId !== oldId) {
    // 신년운세 데이터 클리어
    dataStore.setYearlyFortuneData({});
    store.setYearlyFortuneIndex(1);
    store.setSelectedContentIndex(null);
    // 새 유저 데이터 로드
    await setupInit();
  }
});

/** 상세분석 결제 성공 후 실행 */
const paymentSuccessDo = async (success) => {
  if (success) {
    // 결제 성공 시 팝업 닫기
    showPopAnalysisInfo.value = false;
    showPopAnalysisInfoOther.value = false;
    try {
      // 신년운세 관련 메뉴 체크 (대소문자 무관)
      const menuLower = selectedMenu.value?.toLowerCase() || '';
      const isYearlyFortuneMenu =
        menuLower.includes('yearly') || menuLower.includes('yearlyfortune');

      if (isYearlyFortuneMenu) {
        loadingPersonalityInfo.value = true;
        dataStore.setYearlyFortuneData({});
        store.setYearlyFortuneIndex(1);
        store.setSelectedContentIndex(null);
      }

      await setupInit();
      componentKey.value += 1;
    } catch (error) {
      DEV_MODE && console.error('결제 후 정보 갱신 중 오류:', error);
    }
  }
};

watch(selectedMenu, async (newMenu, oldMenu) => {
  /** 페이지 달라질 때 토글 상태 초기화 */
  toggleTabOpenClass.value = '';

  // 탭 변경 시 해당 typeId로 이용내역 조회
  const newTypeId = getTypeIdByMenu(newMenu);
  const oldTypeId = getTypeIdByMenu(oldMenu);
  if (newTypeId && newTypeId !== oldTypeId) {
    await fetchAnalysisLogData(newTypeId);
  }
});

/** 사주풀이 용어해석 데이터 전송 */
const dictionaryItems = ref(null);
const dictionaryData = ref(null);
const isPageLoading = ref(true);
const fetchSajuWords = async (words) => {
  if (!words || words.length === 0) {
    isPageLoading.value = false;
    return;
  }

  const params = {
    keywordCategory: '용어사전',
    keyword: words,
  };

  try {
    const result = await findTermsAPI(params);
    if (result.data) {
      dictionaryData.value = result.data;
      dictionaryItems.value = words.split(',');
    }
  } catch (e) {
    DEV_MODE && console.error('용어해석 요청에 실패하였습니다:', e);
  } finally {
    isPageLoading.value = false;
  }
};

// ─── 상세분석 서비스 동적 컴포넌트 매핑 ─────────────────────────────────────────

/**
 * 서비스별 컴포넌트 + props 매핑
 * factory 함수는 computed에서 lazy 평가되므로 하단 선언 변수 참조 안전
 */
const SERVICE_VIEW_MAP = {
  sajuSummary: {
    overview: {
      component: CoverPageBasic,
      props: () => ({}),
    },
    detail: {
      component: DetailPageBasic,
      props: () => ({
        isPageLoading: isPageLoading.value,
      }),
      events: () => ({
        sendKeywordParams: fetchSajuWords,
      }),
    },
    overviewPanel: {
      component: CoverPanelBasic,
      props: () => ({}),
      wrapperClasses: () => ['saju-desc', toggleTabOpenClass.value],
    },
    detailPanel: {
      component: DetailPanelBasic,
      condition: () => !!dictionaryData.value,
      props: () => ({
        selectedDescNav: selectedDescNav.value,
        dictionaryItems: dictionaryItems.value,
        dictionaryData: dictionaryData.value,
      }),
      events: () => ({
        'update:selectedDescNav': (v) => {
          selectedDescNav.value = v;
        },
      }),
    },
  },
  personality: {
    overview: {
      component: overviewTendency,
      props: () => ({
        ticketCountData: serviceData.personality.ticketData.value,
        hasPermission: serviceData.personality.permission.value,
        personalityLogData: serviceData.personality.logData.value,
        loadingTicket: loadingPersonalityInfo.value,
      }),
    },
    detail: {
      component: Personality,
      props: () => ({
        hasPermission: serviceData.personality.permission.value,
        updateSelectedDescNav,
      }),
    },
    overviewPanel: {
      component: overviewPaymentInfo,
      props: () => ({
        personalityTicketData: serviceData.personality.ticketData.value,
        personalityLogData: serviceData.personality.logData.value,
        hasPermission: serviceData.personality.permission.value,
      }),
    },
    detailPanel: {
      component: SajuInsightPersPanelRight,
      props: () => ({
        selectedDescNav: selectedDescNav.value,
        guestUserId: guestUserId.value,
      }),
      events: () => ({
        'update:selectedDescNav': (v) => {
          selectedDescNav.value = v;
        },
      }),
    },
  },
  yongsin: {
    overview: {
      component: overviewYongsin,
      props: () => ({
        hasPermission: serviceData.yongsin.permission.value,
        yongsinLogData: serviceData.yongsin.logData.value,
        updateSelectedDescNav,
      }),
    },
    detail: {
      component: youngsin,
      props: () => ({
        hasPermission: serviceData.yongsin.permission.value,
        updateSelectedDescNav,
      }),
    },
    overviewPanel: {
      component: overviewPaymentInfoYoungsin,
      props: () => ({
        yongsinTicketData: serviceData.yongsin.ticketData.value,
        yongsinLogData: serviceData.yongsin.logData.value,
        hasPermission: serviceData.yongsin.permission.value,
      }),
    },
    detailPanel: {
      component: SajuInsightYongsinPanelRight,
      props: () => ({
        selectedDescNav: selectedDescNav.value,
        guestUserId: guestUserId.value,
      }),
      events: () => ({
        'update:selectedDescNav': (v) => {
          selectedDescNav.value = v;
        },
      }),
    },
  },
  yearlyFortune: {
    overview: {
      component: CoverPageYearlyFortune,
      props: () => ({
        ticketCountData: serviceData.yearlyFortune.ticketData.value,
        hasPermission: serviceData.yearlyFortune.permission.value,
        yearlyFortuneLogData: serviceData.yearlyFortune.logData.value,
        loadingTicket: loadingPersonalityInfo.value,
        warningMessage: yearlyFortuneWarningMessage.value,
      }),
      events: () => ({ warning: handleYearlyFortuneWarning }),
    },
    detail: {
      component: DetailPageYearlyFortune,
      extraCondition: () => !loadingPersonalityInfo.value,
      props: () => ({
        hasPermission: serviceData.yearlyFortune.permission.value,
        updateSelectedDescNav,
      }),
      events: () => ({ warning: handleYearlyFortuneWarning }),
    },
    overviewPanel: {
      component: CoverPanelYearlyFortune,
      props: () => ({
        yearlyFortuneTicketData: serviceData.yearlyFortune.ticketData.value,
        yearlyFortuneLogData: serviceData.yearlyFortune.logData.value,
        hasPermission: serviceData.yearlyFortune.permission.value,
      }),
      events: () => ({
        goPopAnalysis: showPopAnalysisInfoDo,
        warning: handleYearlyFortuneWarning,
      }),
    },
    detailPanel: {
      component: SajuInsightPersPanelRight,
      props: () => ({
        selectedDescNav: selectedDescNav.value,
        guestUserId: guestUserId.value,
        showOnlyDictionary: true,
      }),
      events: () => ({
        'update:selectedDescNav': (v) => {
          selectedDescNav.value = v;
        },
      }),
    },
  },
  monthlyFortune: {
    overview: {
      component: overviewMonthlyFortune,
      props: () => ({
        ticketCountData: serviceData.monthlyFortune.ticketData.value,
        hasPermission: serviceData.monthlyFortune.permission.value,
        monthlyFortuneLogData: serviceData.monthlyFortune.logData.value,
        loadingTicket: loadingPersonalityInfo.value,
      }),
      events: () => ({
        'payment-success': paymentSuccessDo,
      }),
    },
    detail: {
      component: MonthlyFortune,
      props: () => ({
        hasPermission: serviceData.monthlyFortune.permission.value,
        updateSelectedDescNav,
      }),
    },
    overviewPanel: {
      component: overviewPaymentInfoMonthlyFortune,
      props: () => ({
        monthlyFortuneTicketData: serviceData.monthlyFortune.ticketData.value,
        monthlyFortuneLogData: serviceData.monthlyFortune.logData.value,
        hasPermission: serviceData.monthlyFortune.permission.value,
      }),
    },
    detailPanel: {
      component: SajuInsightPersPanelRight,
      props: () => ({
        selectedDescNav: selectedDescNav.value,
        guestUserId: guestUserId.value,
      }),
      events: () => ({
        'update:selectedDescNav': (v) => {
          selectedDescNav.value = v;
        },
      }),
    },
  },
  nangmanHarmony: {
    overview: {
      component: CoverPageHarmony,
      props: () => ({
        ticketCountData: serviceData.nangmanHarmony?.ticketData?.value,
        hasPermission: serviceData.nangmanHarmony?.permission?.value,
        harmonyLogData: serviceData.nangmanHarmony?.logData?.value,
        loadingTicket: loadingPersonalityInfo.value,
      }),
    },
    detail: {
      component: DetailPageHarmony,
      props: () => ({
        hasPermission: serviceData.nangmanHarmony?.permission?.value,
      }),
    },
    overviewPanel: {
      component: CoverPanelHarmony,
      props: () => ({
        harmonyTicketData: serviceData.nangmanHarmony?.ticketData?.value,
        harmonyLogData: serviceData.nangmanHarmony?.logData?.value,
        hasPermission: serviceData.nangmanHarmony?.permission?.value,
      }),
    },
    detailPanel: {
      component: DetailPanelHarmony,
      props: () => ({
        harmonyTicketData: serviceData.nangmanHarmony?.ticketData?.value,
        harmonyLogData: serviceData.nangmanHarmony?.logData?.value,
        hasPermission: serviceData.nangmanHarmony?.permission?.value,
      }),
      events: () => ({
        'payment-success': paymentSuccessDo,
      }),
    },
  },
  /** 용신라이트 */
  yongsinLight: {
    overview: {
      component: CoverPageYongsinLight,
      props: () => ({
        hasPermission: serviceData.yongsinLight?.permission?.value,
        yongsinLightLogData: serviceData.yongsinLight?.logData?.value,
        loadingTicket: loadingPersonalityInfo.value,
      }),
    },
    overviewPanel: {
      component: CoverPanelYongsinLight,
      props: () => ({
        guestUserId: guestUserId.value,
        yongsinLightTicketData: serviceData.yongsinLight?.ticketData?.value,
        yongsinLightLogData: serviceData.yongsinLight?.logData?.value,
        hasPermission: serviceData.yongsinLight?.permission?.value,
      }),
    },
    detail: {
      component: DetailPageYongsinLight,
      props: () => ({
        hasPermission: serviceData.yongsinLight?.permission?.value,
        loading: loadingPersonalityInfo.value,
      }),
    },
    detailPanel: {
      component: DetailPanelYongsinLight,
      props: () => ({
        guestUserId: guestUserId.value,
        contentData: store.yongsinLightApiData,
      }),
    },
  },
};

/** 현재 서비스의 메인 컴포넌트 (overview 또는 detail) */
const activeMainView = computed(() => {
  const key = activeServiceKey.value;
  if (!key) return null;

  const cfg = ANALYSIS_SERVICE_CONFIG[key];
  const map = SERVICE_VIEW_MAP[key];
  if (!map) return null;

  const menu = selectedMenu.value;
  const isOverview = menu.startsWith(cfg.overviewPath);
  const isDetail = menu.startsWith(cfg.detailPath);
  if (!isOverview && !isDetail) return null;

  const entry = isOverview ? map.overview : map.detail;
  if (!entry) return null;

  // detail에 추가 조건이 있는 경우 (예: 신년운세 loadingPersonalityInfo 체크)
  if (!isOverview && entry.extraCondition && !entry.extraCondition()) return null;

  return {
    component: entry.component,
    props: entry.props(),
    events: {
      goPopAnalysis: showPopAnalysisInfoDo,
      ...(entry.events ? entry.events() : {}),
    },
  };
});

/** 현재 서비스의 우측 overview 패널 */
const activeOverviewPanel = computed(() => {
  const key = activeServiceKey.value;
  if (!key) return null;

  const map = SERVICE_VIEW_MAP[key];
  if (!map?.overviewPanel) return null;

  const cfg = ANALYSIS_SERVICE_CONFIG[key];
  const menu = selectedMenu.value;
  const isOverview = menu.startsWith(cfg.overviewPath);
  const isDetail = menu.startsWith(cfg.detailPath);

  if (isOverview || (isDetail && tendencyGuide.value)) {
    const entry = map.overviewPanel;
    return {
      component: entry.component,
      props: entry.props(),
      events: entry.events ? entry.events() : { goPopAnalysis: showPopAnalysisInfoDo },
      wrapperClasses: entry.wrapperClasses
        ? entry.wrapperClasses()
        : ['analysis-desc', 'overview-payment-info', toggleTabOpenClass.value],
    };
  }
  return null;
});

/** 현재 서비스의 우측 상세 패널 */
const activeDetailPanel = computed(() => {
  const key = activeServiceKey.value;
  if (!key) return null;

  const map = SERVICE_VIEW_MAP[key];
  if (!map?.detailPanel) return null;

  const cfg = ANALYSIS_SERVICE_CONFIG[key];
  const menu = selectedMenu.value;
  if (!menu.startsWith(cfg.detailPath) || tendencyGuide.value) return null;

  const entry = map.detailPanel;
  if (entry.condition && !entry.condition()) return null;

  return {
    component: entry.component,
    props: entry.props(),
    events: entry.events ? entry.events() : {},
    wrapperClasses: ['analysis-desc', toggleTabOpenClass.value],
  };
});
</script>

<template>
  <!-- ═══ 팝업 ═══ -->
  <PopAnalysisInfo
    :isVisible="showPopAnalysisInfo"
    :analysisType="analysisContent"
    @close="handleClosePop"
    @charge="handleChargePop"
  />
  <PopAnalysisInfoOther
    :isVisible="showPopAnalysisInfoOther"
    :analysisType="analysisContent"
    :ticketCountData="analysisTicketData"
    :ticketCount="analysisTicketCount"
    @close="handleClosePopOther"
    @charge="handleChargePopOtherProduct"
    @payment-success="paymentSuccessDo"
  />

  <!-- ═══ 상세분석 서비스 (동적 컴포넌트) ═══ -->

  <!-- 메인 콘텐츠 -->
  <div class="analysis-main" :class="toggleTabOpenClass" v-if="activeMainView">
    <component
      :is="activeMainView.component"
      :key="componentKey"
      v-bind="activeMainView.props"
      v-on="activeMainView.events"
    />
  </div>

  <!-- 우측 패널: overview (결제정보 / 사주풀이 요약) -->
  <div :class="activeOverviewPanel.wrapperClasses" v-if="activeOverviewPanel">
    <component
      :is="activeOverviewPanel.component"
      :key="componentKey"
      v-bind="activeOverviewPanel.props"
      v-on="activeOverviewPanel.events"
    />
  </div>

  <!-- 우측 패널: detail -->
  <div :class="activeDetailPanel.wrapperClasses" v-if="activeDetailPanel">
    <component
      :is="activeDetailPanel.component"
      v-bind="activeDetailPanel.props"
      v-on="activeDetailPanel.events"
    />
  </div>

  <!-- ═══ 오행도 / Dashboard (미정리) ═══ -->

  <!-- 메인 콘텐츠 -->
  <div
    class="analysis-main"
    :class="toggleTabOpenClass"
    v-if="
      selectedMenu.startsWith('/five') ||
      selectedMenu.startsWith('/fiveguide') ||
      selectedMenu.startsWith('/myfive')
    "
  >
    <Five
      v-if="selectedMenu.startsWith('/five')"
      :birthData="birthData"
      :birthEightTxt="birthEightTxt"
    />
    <FiveGuide
      v-if="selectedMenu.startsWith('/fiveguide')"
      :birthData="birthData"
      :birthEightTxt="birthEightTxt"
      :birthJijanggan="birthJijanggan"
      :selectedDescNavSub="selectedDescNavSub"
    />
    <FiveMy
      v-if="selectedMenu.startsWith('/myfive')"
      :guestUserId="guestUserId"
      :birthData="birthData"
      :birthEightTxt="birthEightTxt"
      :birthJijanggan="birthJijanggan"
      :selectedDescNavSub="selectedDescNavSubMy"
      :selectedDescNav="selectedDescNavMy"
      :langMode="langModeMy"
      @updateLangMode="updateLangModeMy"
    />
  </div>

  <!-- 우측 패널: 오행도 -->
  <div
    class="analysis-desc st-five"
    :class="toggleTabOpenClass"
    v-if="selectedMenu.startsWith('/five')"
  >
    <fiveTab
      :selectedDescNav="selectedDescNav"
      :selectedDescNavSub="selectedDescNavSub"
      :guestUserId="guestUserId"
      @updateSelectedDescNav="updateSelectedDescNav"
      @updateSelectedDescNavSub="updateSelectedDescNavSub"
    />
  </div>
  <div
    class="analysis-desc st-five"
    :class="toggleTabOpenClass"
    v-if="selectedMenu.startsWith('/myfive')"
  >
    <fiveMyTab
      :selectedDescNav="selectedDescNavMy"
      :selectedDescNavSub="selectedDescNavSubMy"
      :guestUserId="guestUserId"
      @updateSelectedDescNavMy="updateSelectedDescNavMy"
      @updateSelectedDescNavSubMy="updateSelectedDescNavSubMy"
      @updateLangMode="updateLangModeMy"
    />
  </div>

  <!-- 토글 버튼 -->
  <div
    class="btn-fold-toggle"
    :class="[toggleTabOpenClass, { 'overview-mode': selectedMenu.startsWith('/overview') }]"
  >
    <button class="btn" @click="toggleTabContent()"></button>
  </div>

  <AlertComponent
    ref="dialogAlert"
    :title="dialogTitle"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
  />
</template>
